import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
// Self-host the Inter variable font so the app actually renders in its
// declared typeface (index.css:214 `font-family: "Inter"`). Without this the
// font was never loaded and every surface silently fell back to system-ui.
// @fontsource-variable bundles the woff2 (no Google-Fonts CDN dependency,
// no extra network request, privacy-clean) and ships `font-display: swap`.
// Imported BEFORE index.css so the @font-face rules are registered first.
import '@fontsource-variable/inter/index.css'
import './index.css'
// Phase 14 — initialize i18next before the app mounts so t() works app-wide
// (side-effect import; configures the singleton in src/i18n/index.ts).
import './i18n'
import App from './App.tsx'
import { refreshAccessToken } from './api/client'

// FB-0001/KI-0006: Auto-recover from stale Vite asset cache after deployments.
// When a new build changes chunk hashes, lazy-loaded routes fail with
// "Failed to fetch dynamically imported module". Detect and auto-reload.
window.addEventListener('error', (event) => {
  const msg = event.message || '';
  if (
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Importing a module script failed') ||
    msg.includes('error loading dynamically imported module')
  ) {
    // Prevent infinite reload loop: only reload once per 10s window
    const lastReload = sessionStorage.getItem('_vite_reload_ts');
    if (!lastReload || Date.now() - parseInt(lastReload, 10) > 10_000) {
      sessionStorage.setItem('_vite_reload_ts', String(Date.now()));
      window.location.reload();
    }
  }
});

// Also catch unhandled promise rejections (dynamic import returns a promise)
window.addEventListener('unhandledrejection', (event) => {
  const reason = String(event.reason || '');
  if (
    reason.includes('Failed to fetch dynamically imported module') ||
    reason.includes('Importing a module script failed') ||
    reason.includes('error loading dynamically imported module')
  ) {
    const lastReload = sessionStorage.getItem('_vite_reload_ts');
    if (!lastReload || Date.now() - parseInt(lastReload, 10) > 10_000) {
      sessionStorage.setItem('_vite_reload_ts', String(Date.now()));
      window.location.reload();
    }
  }
});

// Self-heal a browser whose REAL auth slot was poisoned by a guest JWT.
// Historically the guest-auth flow wrote the guest token into
// `ftl_access_token` (the real session slot); a logged-in user's browser then
// believed it was a guest and every host/meeting action failed client-side
// with "missing access token". Guest tokens are now namespaced to their own
// slot (`ftl_guest_token`), so this can only happen to browsers still carrying
// legacy poison. The fix INSTALLS THE RIGHT TOKEN with zero user action: clear
// only the bad access token, keep the refresh token, and silently mint a fresh
// real access token from it — NO forced re-login (the "browser work" the user
// kept having to do). Render waits on the heal only when poison is found, so
// the common path is unaffected. Guests on /join legitimately hold a guest
// token, so that route is exempt.
async function healClobberedGuestToken(): Promise<void> {
  try {
    const path = window.location.pathname;
    if (path.startsWith('/join')) return;
    const tok = localStorage.getItem('ftl_access_token');
    if (!tok) return;
    const payload = JSON.parse(atob(tok.split('.')[1] || ''));
    if (!payload?.is_guest) return;
    // Drop the wrong-audience guest token from the real slot.
    localStorage.removeItem('ftl_access_token');
    if (localStorage.getItem('ftl_refresh_token')) {
      // Reinstall the user's real session silently. On success
      // refreshAccessToken() writes a fresh `ftl_access_token`; AuthInitializer
      // then loads the user as themselves. On failure the slot stays empty and
      // the normal login flow takes over — still no manual token surgery.
      try { await refreshAccessToken(); } catch { /* fall through to login */ }
    } else {
      // No refresh token → there is no real session to restore; drop the
      // stray user record so the app boots cleanly to login.
      localStorage.removeItem('ftl_user');
    }
  } catch {
    // Malformed token — leave it; the normal auth flow will handle it.
  }
}

// Browser error tracking (Sentry) — opt-in. When VITE_SENTRY_DSN is unset (the
// default), the `if` is statically false at build time so the dynamic import is
// tree-shaken out entirely (zero bundle cost). With a DSN it lazy-loads the SDK
// and initializes it. Fire-and-forget: monitoring must never block the app from
// mounting, and any init failure is swallowed.
async function initSentry(): Promise<void> {
  const env = import.meta.env as Record<string, string | undefined>;
  const dsn = env.VITE_SENTRY_DSN;
  if (!dsn) return;
  try {
    const Sentry = await import('@sentry/react');
    Sentry.init({
      dsn,
      environment: env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE,
      release: env.VITE_SENTRY_RELEASE || undefined,
      tracesSampleRate: Number(env.VITE_SENTRY_TRACES_SAMPLE_RATE || 0),
      sendDefaultPii: false, // multi-tenant: never ship user PII
    });
  } catch {
    /* monitoring is best-effort; never block the app */
  }
}
void initSentry();

healClobberedGuestToken().finally(() => {
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <App />
    </StrictMode>,
  );
});
